/**
 * Which table the seat route is about to deal, said in a URL.
 *
 * ============================ WHY NOT ROUTER STATE =============================
 * A card could hand the table its `Setup` in `navigate(..., { state })` and never put
 * anything in the address bar. Three things that costs, and all three matter here:
 *
 *   1. A reload at the table would land on a page with no table, because router state
 *      does not survive one.
 *   2. A seat could not be linked to — and the saloon's chalkboard, the Learn pages'
 *      "try it" links and the harness all want to link straight to one.
 *   3. A screenshot script would have to click its way in. Phase 3's route shots had
 *      to find and press "Take a seat"; these are one `goto`.
 *
 * So the card's identity and its dials go in the query, and Custom Table — which is a
 * whole `Setup` and not a card — goes in as JSON. The four Level 3 values are NOT in
 * the URL: they are the player's settings, they are read at sit-down, and a link that
 * carried somebody else's seed would be a link that changed your shuffle.
 * ===============================================================================
 */
import { DEFAULT_SETUP, type Setup } from "../lib/setup";
import { PLAY_CARDS, defaultDials, setupFor, type PlayCard } from "./poker-cards";
import { getSavedCards } from "../settings/prefs";
import { level3Setup } from "../settings/SettingsSheet";

export interface Dials { difficulty?: number; seats?: number; depth?: number }

/** `/poker/play/table?card=sng&difficulty=4`. */
export function seatUrl(cardId: string, dials: Dials): string {
  const q = new URLSearchParams({ card: cardId });
  if (dials.difficulty !== undefined) q.set("difficulty", String(dials.difficulty));
  if (dials.seats !== undefined) q.set("seats", String(dials.seats));
  if (dials.depth !== undefined) q.set("depth", String(dials.depth));
  return `/poker/play/table?${q.toString()}`;
}

/** `/poker/play/table?setup=%7B...%7D` — the Custom Table screen's way in. */
export function customSeatUrl(setup: Setup): string {
  return `/poker/play/table?setup=${encodeURIComponent(JSON.stringify(setup))}`;
}

/** A saved card, by its id. The setup itself stays on the device, not in the link. */
export function savedSeatUrl(id: string): string {
  return `/poker/play/table?saved=${encodeURIComponent(id)}`;
}

export interface Resolved {
  setup: Setup;
  /**
   * The query this was read from, verbatim.
   *
   * The seat route deals ONE table per key. React can re-render for a dozen reasons and
   * `setup` is a fresh object every time, so an effect keyed on the object would deal a
   * new table on every render; keyed on this string it deals one per link followed.
   */
  key: string;
  /** What the header calls this table. */
  title: string;
  /** The card it came from, for "Sharpen this" and for the header's line. */
  card: PlayCard | null;
}

/**
 * The query, as a table.
 *
 * Never throws and never returns null: an unreadable link falls back to Quick Play with
 * its own name in the header, because a player who followed a stale link wants a hand,
 * not an error page. The one thing it will not do is silently deal something it was not
 * asked for — the header always says which table this is.
 */
/**
 * The smallest buy-in a custom tournament may name.
 *
 * A price is either a real one or a lie, and a 1-Buck tournament with a 9-Buck pool is
 * closer to the second: it looks like a price and costs nothing, which is exactly the
 * hole `Setup.buyin` was added to close. Twenty is a tenth of the Sit & Go's, which is
 * as cheap as a seat gets on any of the named cards.
 */
export const MIN_CUSTOM_BUYIN = 20;

/**
 * A tournament setup that came in over a link or out of storage always has a price.
 *
 * The Custom screen's own field is clamped, but a setup can also arrive as JSON in a
 * query string or out of a card saved by an older build — and both of those are, in
 * effect, user input. A tournament with a missing, zero or negative buy-in is given the
 * default rather than refused, because refusing sends a player who followed a link to
 * an error page, and the header already says which table this is.
 */
function withBuyin(s: Setup): Setup {
  if (s.format !== "tournament") return s;
  const v = Math.round(Number(s.buyin));
  return Number.isFinite(v) && v >= MIN_CUSTOM_BUYIN ? { ...s, buyin: v } : { ...s, buyin: DEFAULT_SETUP.buyin };
}

export function resolveSeat(search: string): Resolved {
  const q = new URLSearchParams(search);
  const level3 = level3Setup();

  const raw = q.get("setup");
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<Setup>;
      const quick = PLAY_CARDS[0]!;
      const base = setupFor(quick, defaultDials(quick), level3);
      const setup = { ...base, ...parsed, ...level3 };
      return { setup: withBuyin(setup), title: "Custom table", card: null, key: search };
    } catch { /* fall through to the card path */ }
  }

  const savedId = q.get("saved");
  if (savedId) {
    const saved = getSavedCards().find((c) => c.id === savedId);
    if (saved) {
      const quick = PLAY_CARDS[0]!;
      const base = setupFor(quick, defaultDials(quick), level3);
      return {
        setup: withBuyin({ ...base, ...(saved.setup as Partial<Setup>), ...level3 }),
        title: saved.name,
        card: null,
        key: search,
      };
    }
  }

  /*
   * A card that is still being verified has no table behind it.
   *
   * Its Play button does not exist, but its URL is constructible — and dealing an
   * ordinary 9-max cash game under the heading "Bounty Hunter" would be exactly the
   * claim the verifying state exists to avoid making. It falls back to Quick Play, by
   * name, so the header says what was actually dealt.
   */
  const asked = PLAY_CARDS.find((c) => c.id === q.get("card"));
  const card = asked && !asked.verifying ? asked : PLAY_CARDS[0]!;
  const dials = { ...defaultDials(card) };
  const num = (k: string): number | undefined => {
    const v = Number(q.get(k));
    return Number.isFinite(v) && q.get(k) !== null ? v : undefined;
  };
  if (num("difficulty") !== undefined) dials.difficulty = num("difficulty");
  if (num("seats") !== undefined) dials.seats = num("seats");
  if (num("depth") !== undefined) dials.depth = num("depth");
  return { setup: setupFor(card, dials, level3), title: card.title, card, key: search };
}
